import { ApiResult } from "src/api/types";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import { listMappingTransform, toDisplayTime } from "src/utils/helper";

import {
  OperationType,
  RentResourceBatchWalletStatus,
} from "@wallet-manager/node-types/dist/src/postgres/const";

import { GetListRes } from "../types/GetListRes";
import { TableColumnKey, TableRow } from "../types/Table";
import { useAppSelector } from "src/reducer/hooks";
import { selectCrossMerchant } from "src/reducer/crossMerchantSlice";

interface UseTableRowsProps {
  apiList: ApiResult<GetListRes>;
}

type MapResultToTupleArray = (
  row: GetListRes[],
  omitKeys: string[],
) => any[][][];

const useTableRows = (props: UseTableRowsProps) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, te } = useTranslation(translatePrefix);
  const { merchantMapping } = useAppSelector(selectCrossMerchant);
  const getMerchantName = (merchantId: string) => merchantMapping?.[merchantId];
  const { apiList } = props;

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((item) => {
      const operationType = item?.wallet_operation_batchs?.[0]?.operation_type;

      const getFreeBandwidthDisplayValue = () => {
        if (operationType !== OperationType.Sweep) {
          return;
        }

        return item.sweep_requests?.tron_free_bandwidth_only
          ? te("yes")
          : te("no");
      };

      const mappedResult = [
        [TableColumnKey.BatchId, t(TableColumnKey.BatchId), item?.batch_id],
        [
          TableColumnKey.MerchantName,
          t(TableColumnKey.MerchantName),
          getMerchantName(item.merchant_id),
        ],
        [
          TableColumnKey.ChainName,
          t(TableColumnKey.ChainName),
          // TODO: [tron-record] remove later, hard code for QA
          "TRON",
          // findChainInfo(item.chain_type, item.chain_id)?.name, //TODO show chain name when api ready
        ],
        [
          TableColumnKey.OperationType,
          t(TableColumnKey.OperationType),
          te(OperationType[operationType] as any),
        ],
        [TableColumnKey.StepSeq, t(TableColumnKey.StepSeq), item.step_seq],
        [
          TableColumnKey.TransactionSequence,
          t(TableColumnKey.TransactionSequence),
          item?.tx_seq,
        ],
        [
          TableColumnKey.FreeBandwidthOnly,
          t(TableColumnKey.FreeBandwidthOnly),
          getFreeBandwidthDisplayValue(),
        ],
        [
          TableColumnKey.WalletAddress,
          t(TableColumnKey.WalletAddress),
          item?.wallet_address,
        ],
        [
          TableColumnKey.Status,
          t(TableColumnKey.Status),
          te(RentResourceBatchWalletStatus[item?.status] as any),
        ],
        [
          TableColumnKey.ActualRequiredEnergy,
          t(TableColumnKey.ActualRequiredEnergy),
          `${item?.actual_energy ?? ""}/ ${item?.required_energy ?? ""}`,
        ],
        [
          TableColumnKey.actualFreeActualDelegateRequiredBandwidth,
          t(TableColumnKey.actualFreeActualDelegateRequiredBandwidth),
          `${item?.actual_bandwidth ?? ""}/ ${
            item?.actual_delegated_bandwidth ?? ""
          }/ ${item?.required_bandwidth ?? ""}`,
        ],
        [
          TableColumnKey.ActualEnergy,
          t(TableColumnKey.ActualEnergy),
          item?.actual_energy,
        ],
        [
          TableColumnKey.ActualBandwidth,
          t(TableColumnKey.ActualBandwidth),
          item?.actual_bandwidth,
        ],
        [
          TableColumnKey.RequiredEnergy,
          t(TableColumnKey.RequiredEnergy),
          item?.required_energy,
        ],
        [
          TableColumnKey.RequiredBandwidth,
          t(TableColumnKey.RequiredBandwidth),
          item?.required_bandwidth,
        ],
        [
          TableColumnKey.RequestedEnergy,
          t(TableColumnKey.RequestedEnergy),
          item?.requested_energy,
        ],
        [
          TableColumnKey.RequestedEnergyCost,
          t(TableColumnKey.RequestedEnergyCost),
          item?.requested_energy_cost,
        ],
        [
          TableColumnKey.RequestedBandwidth,
          t(TableColumnKey.RequestedBandwidth),
          item?.requested_bandwidth,
        ],
        [
          TableColumnKey.RequestedBandwidthCost,
          t(TableColumnKey.RequestedBandwidthCost),
          item?.requested_bandwidth_cost,
        ],
        // [
        //   TableColumnKey.LastBandwidthBlockNumber,
        //   t(TableColumnKey.LastBandwidthBlockNumber),
        //   item.last_bandwidth_block_number,
        // ],
        [
          TableColumnKey.LastFreeBandwidth,
          t(TableColumnKey.LastFreeBandwidth),
          item?.last_bandwidth,
        ],
        [
          TableColumnKey.LastDelegateBandwidth,
          t(TableColumnKey.LastDelegateBandwidth),
          item?.last_delegated_bandwidth,
        ],
        // [
        //   TableColumnKey.LastEnergyBlockNumber,
        //   t(TableColumnKey.LastEnergyBlockNumber),
        //   item.last_energy_block_number,
        // ],
        [
          TableColumnKey.LastEnergy,
          t(TableColumnKey.LastEnergy),
          item?.last_energy,
        ],
        [
          TableColumnKey.CreationTime,
          t(TableColumnKey.CreationTime),
          toDisplayTime(item?.created_date),
        ],
        [
          TableColumnKey.LastModifiedTime,
          t(TableColumnKey.LastModifiedTime),
          toDisplayTime(item?.last_modified_date),
        ],
        ["rawData", "", item],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList.rows, []);
  const rows: TableRow[] = res.map(listMappingTransform("key"));

  return {
    rows,
    totalCounts: apiList.count,
    mapResultToTupleArray,
  };
};

export default useTableRows;
